import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  MapPin,
  DollarSign,
  Trash,
  ChevronRight,
  Briefcase,
} from "lucide-react";
import {
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";

import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import useCategories from "../../hooks/useCategories";

const STATUS_TABS = ["all", "open", "assigned", "completed", "cancelled"];

export default function MyRequests() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories } = useCategories();

  const [filter, setFilter] = useState("all");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskers, setTaskers] = useState({});
  const [offerCounts, setOfferCounts] = useState({});

  useEffect(() => {
    if (!user?.uid) return;

    setLoading(true);

    const serviceQuery = query(
      collection(db, "serviceRequests"),
      where("customerId", "==", user.uid)
    );

    const jobsQuery = query(
      collection(db, "jobs"),
      where("customerId", "==", user.uid)
    );

    const unsubService = onSnapshot(serviceQuery, (snap) => {
      const serviceData = snap.docs
        .map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            type: "request",
            customerId: data.customerId || "",
            categoryId: data.categoryId || "",
            subcategoryId: data.subcategoryId || "",
            title: data.subcategoryId || "",
            description: data.description || "",
            locationText: data.locationText || "",
            preferredDate: data.preferredDate || "",
            preferredTime: data.preferredTime || "",
            budget: data.budget ?? "",
            status: data.status || "",
          };
        })
        .filter((item) => item.status !== "assigned");

      setRequests((prev) => mergeData(prev, serviceData, "request"));
      setLoading(false);
    });

    const unsubJobs = onSnapshot(jobsQuery, async (snap) => {
      const jobsData = [];
      const taskerIds = new Set();

      snap.docs.forEach((docSnap) => {
        const data = docSnap.data();

        jobsData.push({
          id: docSnap.id,
          type: "job",
          customerId: data.customerId || "",
          taskerId: data.taskerId || "",
          categoryId: data.categoryId || "",
          subcategoryId: data.subcategoryId || "",
          title: data.subcategoryId || "",
          description: data.note || "",
          locationText: data.locationText || "",
          preferredDate: data.preferredDate || "",
          preferredTime: data.preferredTime || "",
          budget: data.price ?? "",
          status: data.status || "",
        });

        if (data.taskerId) taskerIds.add(data.taskerId);
      });

      const newTaskers = { ...taskers };

      await Promise.all(
        [...taskerIds].map(async (taskerId) => {
          if (!newTaskers[taskerId]) {
            const taskerSnap = await getDoc(doc(db, "taskers", taskerId));
            if (taskerSnap.exists()) {
              newTaskers[taskerId] = taskerSnap.data();
            }
          }
        })
      );

      setTaskers(newTaskers);
      setRequests((prev) => mergeData(prev, jobsData, "job"));
      setLoading(false);
    });

    return () => {
      unsubService();
      unsubJobs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;

    const offersQuery = query(collection(db, "taskerOffers"));

    const unsubOffers = onSnapshot(offersQuery, (snap) => {
      const counts = {};

      snap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const requestId = data.requestedId;
        if (!requestId) return;

        counts[requestId] = (counts[requestId] || 0) + 1;
      });

      setOfferCounts(counts);
    });

    return () => unsubOffers();
  }, [user?.uid]);

  function mergeData(prev, newData, type) {
    const remaining = prev.filter((item) => item.type !== type);
    return [...remaining, ...newData];
  }

  const handleDelete = async (item) => {
    if (["assigned", "completed", "cancelled"].includes(item.status)) return;

    const collectionName = item.type === "job" ? "jobs" : "serviceRequests";
    await deleteDoc(doc(db, collectionName, item.id));
  };

  const handleClick = (item) => {
    if (item.status === "draft" || item.status === "pending_payment") {
      if (item.type === "job") {
        navigate(`/customer/booking/job/${item.id}`);
      } else {
        navigate(`/customer/booking/request/${item.id}`);
      }
      return;
    }

    if (item.status === "completed" || item.status === "cancelled" || item.status === "assigned") {
      navigate(`/customer/order-status/${item.id}`);
    }
  };

  const handleViewOffers = (item) => {
    navigate(`/customer/view-offers/${item.id}`);
  };

  const filtered = requests.filter((item) =>
    filter === "all" ? true : item.status === filter
  );

  const categoryMap = useMemo(() => {
    const map = {};

    (categories || []).forEach((category) => {
      (category.subcategories || []).forEach((sub) => {
        map[sub.id] = {
          categoryId: category.id,
          categoryName: category.name || "",
          categoryIcon: category.icon || null,
          subcategoryName: sub.name || sub.id || "",
        };
      });
    });

    return map;
  }, [categories]);

  const getCategoryMeta = (item) => {
    return (
      categoryMap[item.subcategoryId] || {
        categoryId: item.categoryId || "",
        categoryName: "",
        categoryIcon: null,
        subcategoryName: item.subcategoryId || item.title || "",
      }
    );
  };

  const statusBadgeClass = (status) => {
    switch (status) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "assigned":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-emerald-100 text-emerald-800";
      case "cancelled":
        return "bg-gray-200 text-gray-700";
      case "draft":
        return "bg-amber-200 text-amber-800";
      case "pending_payment":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const formatPreferredDate = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value?.toDate) return value.toDate().toLocaleDateString();
    return String(value);
  };

  const fallbackText = (value, fieldName, entityName) => {
    if (value === 0) return "0";
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return value;
    }
    return `This ${entityName} have no ${fieldName}`;
  };

  const budgetText = (value, entityName) => {
    if (value === 0 || value === "0") return "0 SAR";
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return `${value} SAR`;
    }
    return `This ${entityName} have no budget`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-[#1E3A8A]">My Service Requests</h2>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {STATUS_TABS.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filter === status
                  ? "bg-[#006C35] text-white"
                  : "bg-white border border-gray-300 text-gray-700"
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center">
            <p className="text-gray-500">No requests found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((req) => {
              const meta = getCategoryMeta(req);
              const offerCount = offerCounts[req.id] || 0;
              const isTitleClickable = ["draft", "pending_payment", "assigned", "completed", "cancelled"].includes(
                req.status
              );

              return (
                <div
                  key={`${req.type}-${req.id}`}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                >
                  {req.type === "request" ? (
                  <>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="min-w-0">
                        <h3
                          className={`text-[20px] font-medium text-[#1E3A8A] ${
                            isTitleClickable ? "cursor-pointer hover:underline" : "cursor-default"
                          }`}
                          onClick={() => {
                            if (isTitleClickable) handleClick(req);
                          }}
                        >
                          {fallbackText(
                            meta.subcategoryName || req.title,
                            "service title",
                            "Service Request"
                          )}
                        </h3>

                        <div className="mt-2 flex items-center gap-2 text-[15px] text-[#6B7280]">
                          {meta.categoryIcon ? (
                            <span className="text-base leading-none">{meta.categoryIcon}</span>
                          ) : (
                            <Briefcase size={14} />
                          )}
                          <span>
                            {fallbackText(meta.categoryName, "category", "Service Request")}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-3 shrink-0">
                        <span
                          className={`px-5 py-1.5 rounded-full text-sm font-medium ${statusBadgeClass(
                            req.status
                          )}`}
                        >
                          {req.status}
                        </span>

                        {req.status === "open" && offerCount > 0 && (
                          <button
                            onClick={() => handleViewOffers(req)}
                            className="bg-[#006C35] text-white px-4 py-2 rounded-full text-sm font-medium"
                          >
                            {offerCount} offers
                          </button>
                        )}
                      </div>
                    </div>

                    <p className="text-[16px] text-[#374151] mb-5 leading-7">
                      {fallbackText(req.description, "description", "Service Request")}
                    </p>

                    <div className="flex flex-wrap items-center gap-5 text-[15px] text-[#4B5563] mb-6">
                      <div className="flex items-center gap-2">
                        <MapPin size={18} />
                        <span>
                          {fallbackText(req.locationText, "location", "Service Request")}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Clock size={18} />
                        <span>
                          {fallbackText(
                            formatPreferredDate(req.preferredDate),
                            "preferred date",
                            "Service Request"
                          )}{" "}
                          at{" "}
                          {fallbackText(
                            req.preferredTime,
                            "preferred time",
                            "Service Request"
                          )}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <DollarSign size={18} />
                        <span>Budget: {budgetText(req.budget, "Service Request")}</span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-5 flex items-center justify-between">
                      {req.status === "open" ? (
                        <button
                          onClick={() => handleViewOffers(req)}
                          className="text-[#006C35] text-[16px] font-medium hover:underline flex items-center gap-1"
                        >
                          Click to view and accept offers <ChevronRight size={16} />
                        </button>
                      ) : (
                        <div />
                      )}

                      {!["assigned", "completed", "cancelled"].includes(req.status) && (
                        <button
                          className="text-red-600 flex items-center gap-2 text-[16px] hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDelete(req);
                          }}
                        >
                          <Trash size={16} />
                          Delete
                        </button>
                      )}
                    </div>
                  </>
                  ) : (
                    <>
                      {req.type === "job" && taskers[req.taskerId] && (
                        <div className="flex items-center gap-3 mb-3">
                          <img
                            src={
                              taskers[req.taskerId].imageUrl ||
                              "https://via.placeholder.com/40"
                            }
                            alt="tasker"
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <span className="text-sm font-medium text-[#1E3A8A]">
                            {fallbackText(
                              taskers[req.taskerId].name,
                              "tasker name",
                              "Job"
                            )}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between mb-3">
                        <h3
                          className={`text-[#1E3A8A] ${
                            isTitleClickable
                              ? "cursor-pointer hover:underline"
                              : "cursor-default"
                          }`}
                          onClick={() => {
                            if (isTitleClickable) handleClick(req);
                          }}
                        >
                          {fallbackText(
                            meta.subcategoryName || req.title,
                            "service title",
                            "Job"
                          )}
                        </h3>

                        <span
                          className={`px-3 py-1 rounded-full text-sm ${statusBadgeClass(
                            req.status
                          )}`}
                        >
                          {req.status}
                        </span>
                      </div>

                      <p className="text-gray-600 mb-4">
                        {fallbackText(req.description, "description", "Job")}
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <MapPin size={16} />
                          <span>
                            {fallbackText(req.locationText, "location", "Job")}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span>
                            {fallbackText(
                              formatPreferredDate(req.preferredDate),
                              "preferred date",
                              "Job"
                            )}{" "}
                            at{" "}
                            {fallbackText(
                              req.preferredTime,
                              "preferred time",
                              "Job"
                            )}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <DollarSign size={16} />
                          <span>{budgetText(req.budget, "Job")}</span>
                        </div>
                      </div>

                      {!["assigned", "completed", "cancelled"].includes(
                        req.status
                      ) && (
                        <button
                          className="mt-4 text-red-600 flex items-center gap-2 cursor-pointer hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDelete(req);
                          }}
                        >
                          <Trash size={16} />
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}